import LegalLayout, { Section } from "@/components/legal/LegalLayout";
import usePageMeta from "@/hooks/usePageMeta";

export default function Privacy() {
  usePageMeta({
    title: "Privacy Policy — ColourMatch AI Paint Visualiser",
    description: "How ColourMatch (an AI paint visualiser software platform for Australian homeowners) collects, uses and protects your personal information, aligned to the Australian Privacy Principles.",
    path: "/privacy",
  });
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated="February 2026"
      testid="privacy-page"
      intro={
        <span><strong>Draft for review.</strong> Aligned to the Australian Privacy Principles. Please have an Australian privacy lawyer review before live trading.</span>
      }
    >
      <Section heading="1. Who we are">
        <p>
          ColourMatch is an AI paint visualisation service operated from Australia. This Privacy
          Policy explains what personal information we collect, how we use it, and your rights
          under the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).
        </p>
      </Section>

      <Section heading="2. Information we collect">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Account information</strong> — name, email, password (hashed), optional phone.</li>
          <li><strong>Uploaded photos</strong> — images you upload to render.</li>
          <li><strong>Render data</strong> — surface and colour selections, AI output metadata.</li>
          <li><strong>Payment metadata</strong> — Stripe session and status; we do <em>not</em> see or store your card number.</li>
          <li><strong>Technical data</strong> — IP address, browser, device, basic analytics.</li>
        </ul>
      </Section>

      <Section heading="3. How we use your information">
        <ul className="list-disc pl-5 space-y-1">
          <li>To deliver the visualisation service (uploading photos to our AI provider for processing);</li>
          <li>To authenticate you and protect your account;</li>
          <li>To process payments via Stripe;</li>
          <li>To respond to support enquiries;</li>
          <li>To improve service quality and detect abuse;</li>
          <li>To comply with legal obligations.</li>
        </ul>
      </Section>

      <Section heading="4. Third-party services we use">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Google (Gemini)</strong> — receives your photo and prompt to generate the recoloured image. Subject to Google's privacy terms.</li>
          <li><strong>Stripe</strong> — processes payments. Subject to Stripe's privacy policy.</li>
          <li><strong>Emergent Object Storage</strong> — stores your photos and renders securely.</li>
          <li><strong>MongoDB</strong> — stores account and project metadata.</li>
        </ul>
      </Section>

      <Section heading="5. Where your data is stored">
        <p>
          Your data may be processed in Australia, the United States, the European Union or
          other countries where our cloud providers operate. We require providers to apply
          security standards consistent with the Australian Privacy Principles.
        </p>
      </Section>

      <Section heading="6. How long we keep it">
        <p>
          We keep account and project data for as long as your account is active, plus a
          reasonable period after closure to comply with legal obligations (typically 7 years
          for financial records). You may request earlier deletion of your photos and renders
          at any time.
        </p>
      </Section>

      <Section heading="7. Your rights">
        <ul className="list-disc pl-5 space-y-1">
          <li>Access the personal information we hold about you;</li>
          <li>Correct inaccurate information;</li>
          <li>Request deletion of your account and associated photos;</li>
          <li>Withdraw consent for non-essential processing (e.g. analytics);</li>
          <li>Make a complaint to the Office of the Australian Information Commissioner (OAIC) at <a className="underline" href="https://www.oaic.gov.au" target="_blank" rel="noreferrer">oaic.gov.au</a>.</li>
        </ul>
        <p>To exercise any of these rights, email <strong>privacy@colourmatch.au</strong>.</p>
      </Section>

      <Section heading="8. Cookies and analytics">
        <p>
          We use a minimal set of cookies / local storage to keep you signed in. We do not sell
          your data. Any analytics we use is configured to anonymise IP addresses.
        </p>
      </Section>

      <Section heading="9. Security">
        <p>
          Passwords are stored as bcrypt hashes. Sessions use signed JWT tokens with limited
          lifetime. Photos and renders are stored in encrypted object storage. We will notify
          affected users without undue delay in the event of an eligible data breach as
          required by the Notifiable Data Breaches scheme.
        </p>
      </Section>

      <Section heading="10. Children">
        <p>
          ColourMatch is not directed to children under 16. If we become aware that we have
          collected personal information from a child without parental consent, we will delete
          it.
        </p>
      </Section>

      <Section heading="11. Changes to this policy">
        <p>
          We may update this Privacy Policy. The “Last updated” date above will reflect the
          latest version. Material changes will be notified by email or in-app.
        </p>
      </Section>

      <Section heading="12. Contact us">
        <p>
          For any privacy enquiries: <strong>privacy@colourmatch.au</strong>
        </p>
      </Section>
    </LegalLayout>
  );
}
